<template>
  <div class="form-page-container">
    <PremiumPage
      title="Add Request Approval Access"
      subtitle="Assign a member responsible for approving society requests"
      showBack
      :loading="saveLoading"
      :show-footer-buttons="true"
      submit-label="Submit"
      cancel-label="Cancel"
      @submit="savePerson"
      @cancel="$router.go(-1)"
    >
      <!-- ── STEP 1 ── -->
      <div class="form-section">
        <div class="half-width-wrap">
          <div v-if="useBlocks" class="mb-4">
            <PremiumSelect
              v-model="committee.flat_id"
              label="Filter by block"
              :items="flats"
              item-text="name"
              item-value="id"
              placeholder="Select block (optional)"
              icon="mdi-office-building-outline"
              clearable
              name="flat"
              :error="serverSideErrors.flat_id"
              @change="getFlatMember"
            />
            <div class="hint-text" v-if="committee.flat_id === 'all_shops'">All shop members are shown</div>
            <div class="hint-text" v-else-if="committee.flat_id">Only members from the selected block are shown</div>
          </div>
          <div class="mb-2">
            <PremiumSelect
              v-model="committee.member_id"
              label="Member *"
              :items="members"
              item-text="display_name"
              item-value="id"
              placeholder="Select member"
              icon="mdi-account-outline"
              clearable
              v-validate="'required'"
              name="member_id"
              data-vv-as="Member"
              :error="errors.first('member_id') || serverSideErrors.member_id || serverSideErrors.unit_mapping_id"
              @change="onMemberChange"
            />
          </div>
        </div>
      </div>

      <div class="form-divider" />

      <!-- ── STEP 2 ── -->
      <div class="form-section">
        <div class="scope-question">Which incoming requests can this member approve?</div>

        <div class="scope-grid">

          <!-- ALL REQUESTS — exclusive (round radio), hidden for Commercial+No Building -->
          <div
            v-if="showAllRequests"
            class="scope-card scope-card--full"
            :class="{ 'scope-card--active': isAllSelected }"
            @click="toggleAll"
          >
            <div class="scope-card-left">
              <div class="scope-checkbox" :class="{ checked: isAllSelected }">
                <v-icon v-if="isAllSelected" size="14" color="#fff">mdi-check</v-icon>
              </div>
              <div>
                <div class="scope-title">All requests</div>
                <div class="scope-desc">Approve every new member{{ isMixed ? ' &amp; shop' : '' }} request across the society</div>
              </div>
            </div>
            <span class="scope-badge">FULL</span>
          </div>

          <!-- ALL HOUSES — only Mixed, always radio (round) -->
          <div
            v-if="showAllHouses"
            class="scope-card"
            :class="{ 'scope-card--active': isScopeChecked('all_houses') }"
            @click="toggleScope('all_houses')"
          >
            <div class="scope-checkbox" :class="{ checked: isScopeChecked('all_houses') }">
              <v-icon v-if="isScopeChecked('all_houses')" size="14" color="#fff">mdi-check</v-icon>
            </div>
            <div class="scope-title">All houses</div>
            <div class="scope-side-hint">No shops</div>
          </div>

          <!-- ONLY SHOP — only Mixed, always radio (round) -->
          <div
            v-if="showOnlyShop"
            class="scope-card"
            :class="{ 'scope-card--active': isScopeChecked('only_shop') }"
            @click="toggleScope('only_shop')"
          >
            <div class="scope-checkbox" :class="{ checked: isScopeChecked('only_shop') }">
              <v-icon v-if="isScopeChecked('only_shop')" size="14" color="#fff">mdi-check</v-icon>
            </div>
            <div class="scope-title">Only shop</div>
          </div>

          <!-- BLOCKS — per-block square checkboxes, only when has_multiple_building -->
          <template v-if="useBlocks">
            <div
              v-for="flat in blockScopeFlats"
              :key="'scope-' + flat.id"
              class="scope-card"
              :class="{ 'scope-card--active': isBlockChecked(flat.id) }"
              @click="toggleBlock(flat.id)"
            >
              <div class="scope-checkbox scope-checkbox--sq" :class="{ checked: isBlockChecked(flat.id) }">
                <v-icon v-if="isBlockChecked(flat.id)" size="14" color="#fff">mdi-check</v-icon>
              </div>
              <div class="scope-title">{{ flat.name }}</div>
            </div>
          </template>

        </div>


      </div>

    </PremiumPage>
  </div>
</template>

<script>
import { mapMutations, mapGetters, mapState } from "vuex";
import PremiumPage from "../../common/PremiumPage.vue";
import PremiumSelect from "../../common/PremiumSelect.vue";
import { mapMembersWithDisplayName } from "@/helpers/memberDisplay";

export default {
  name: "AddResponsiblePerson",
  components: { PremiumPage, PremiumSelect },

  data() {
    return {
      saveLoading: false,
      serverSideErrors: {},
      flats: [],
      members: [],
      committee: {
        flat_id: "",
        member_id: "",
        unit_mapping_id: "",
        selectedScopes: ["all"],   // array: 'all' | 'all_houses' | 'only_shop' | 'blocks'
        selectedBlockIds: [],      // flat_ids when 'blocks' in selectedScopes
      },
    };
  },

  computed: {
    ...mapGetters("society", ["useBlocks", "unitType"]),
    ...mapState("society", ["hasMultipleBuilding"]),
    ...mapState("theme", ["primaryColor"]),

    isResidential() { return this.unitType == 1; },
    isCommercial()  { return this.unitType == 2; },
    isMixed()       { return this.unitType == 3; },

    // Residential has no shops → "All requests" = "All houses", so hide "All houses"
    showAllRequests() { return true; },
    showAllHouses()   { return this.isMixed; },
    showOnlyShop()    { return this.isMixed; },

    isAllSelected() { return this.committee.selectedScopes.includes('all'); },

    blockScopeFlats() {
      return this.flats.filter((f) => f.id !== 'all_shops');
    },

    scopeSummaryLabel() {
      if (this.isAllSelected) return 'All scopes (full society)';
      const parts = [];
      if (this.committee.selectedScopes.includes('all_houses')) parts.push('All houses');
      if (this.committee.selectedScopes.includes('only_shop'))  parts.push('Only shop');
      if (this.committee.selectedScopes.includes('blocks')) {
        const names = this.committee.selectedBlockIds.map(id => {
          const f = this.blockScopeFlats.find(fl => fl.id == id);
          return f ? f.name : id;
        });
        parts.push(...names);
      }
      return parts.length ? parts.join(' + ') : 'No scope selected';
    },
  },

  watch: {
    unitType:            { immediate: true, handler() { this.setDefaultScopes(); } },
    hasMultipleBuilding: { immediate: true, handler() { this.setDefaultScopes(); } },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    setDefaultScopes() {
      this.committee.selectedScopes = ['all'];
      this.committee.selectedBlockIds = [];
    },

    // "All requests" — exclusive toggle (round)
    toggleAll() {
      if (this.isAllSelected) {
        this.committee.selectedScopes = [];
      } else {
        this.committee.selectedScopes = ['all'];
        this.committee.selectedBlockIds = [];
      }
    },

    // Independent scope toggle (square)
    isScopeChecked(key) { return this.committee.selectedScopes.includes(key); },
    toggleScope(key) {
      const arr = this.isAllSelected ? [] : [...this.committee.selectedScopes];

      // Mixed: "all_houses" and "only_shop" are always mutually exclusive (radio)
      if (this.isMixed && (key === 'all_houses' || key === 'only_shop')) {
        const alreadySelected = arr.includes(key);
        // Remove both, then add clicked one (unless it was already selected → deselect)
        const filtered = arr.filter(k => k !== 'all_houses' && k !== 'only_shop');
        if (!alreadySelected) filtered.push(key);
        this.committee.selectedScopes = filtered;
        this.autoUpgradeToAll();
        return;
      }

      const idx = arr.indexOf(key);
      if (idx === -1) arr.push(key); else arr.splice(idx, 1);
      this.committee.selectedScopes = arr;
      this.autoUpgradeToAll();
    },

    // Block toggle (square, multi)
    isBlockChecked(flatId) {
      return this.committee.selectedScopes.includes('blocks') &&
             this.committee.selectedBlockIds.includes(flatId);
    },
    toggleBlock(flatId) {
      const scopes = this.isAllSelected ? [] : [...this.committee.selectedScopes];
      const ids = [...this.committee.selectedBlockIds];
      const idx = ids.indexOf(flatId);
      if (idx === -1) ids.push(flatId); else ids.splice(idx, 1);

      if (ids.length > 0 && !scopes.includes('blocks')) scopes.push('blocks');
      if (ids.length === 0) { const bi = scopes.indexOf('blocks'); if (bi !== -1) scopes.splice(bi, 1); }
      this.committee.selectedScopes = scopes;
      this.committee.selectedBlockIds = ids;
      this.autoUpgradeToAll();
    },

    // If all available options are selected → auto upgrade to 'all'
    autoUpgradeToAll() {
      const scopes = this.committee.selectedScopes;
      if (scopes.includes('all')) return;

      const available = [];
      if (this.showAllHouses) available.push('all_houses');
      if (this.showOnlyShop)  available.push('only_shop');
      if (this.useBlocks)     available.push('blocks');

      if (available.length === 0) return;

      const allScopesCovered = available.every(k => scopes.includes(k));
      const allBlocksCovered = !this.useBlocks ||
        (scopes.includes('blocks') && this.blockScopeFlats.every(f => this.committee.selectedBlockIds.includes(f.id)));

      if (allScopesCovered && allBlocksCovered) {
        this.committee.selectedScopes = ['all'];
        this.committee.selectedBlockIds = [];
      }
    },

    applyFlatsList(flats) {
      let list = [...(flats || [])];
      if (this.isMixed && this.useBlocks) {
        list.unshift({ id: 'all_shops', name: 'All Shops' });
      }
      this.flats = list;
    },

    getFlats() {
      this.$axios.get("societyadmin/flats").then((response) => {
        const data = response.data;
        if (data.status) this.applyFlatsList(data.flats);
        else this.showError(data.message);
      });
    },

    getFlatMember() {
      const flatId = this.committee.flat_id || 'null';
      this.committee.member_id = "";
      this.committee.unit_mapping_id = "";
      this.$axios.get("/societyadmin/requestApproval/" + flatId + "/members").then((response) => {
        const data = response.data;
        if (data.status) {
          this.members = mapMembersWithDisplayName(data.members, this.unitType);
        } else this.showError(data.message);
      });
    },

    onMemberChange(val) {
      const selected = val ? this.members.find((m) => m.id === val) : null;
      this.committee.unit_mapping_id = selected ? (selected.unit_mapping_id || "") : "";
    },

    savePerson() {
      this.$validator.validateAll().then((success) => {
        if (!success) return;

        const scopes = this.committee.selectedScopes;
        if (!scopes || scopes.length === 0) {
          this.showError('Please select at least one approval scope.');
          return;
        }
        if (scopes.includes('blocks') && this.committee.selectedBlockIds.length === 0) {
          this.showError('Please select at least one block.');
          return;
        }

        this.saveLoading = true;
        const payload = {
          member_id:          this.committee.member_id,
          flat_id:            this.committee.flat_id || null,
          unit_mapping_id:    this.committee.unit_mapping_id || null,
          approval_scopes:    scopes.join(','),
          approval_block_ids: scopes.includes('blocks')
            ? this.committee.selectedBlockIds.join(',')
            : null,
        };

        this.$axios.post("/societyadmin/flatResponsiblePersons", payload)
          .then((response) => {
            this.saveLoading = false;
            const data = response.data;
            if (data.status) {
              this.setAlert({ showAlert: true, variant: "green accent-4", timeout: "2000", message: data.message });
              this.$router.push("/societyadmin/responsibleperson");
            } else this.handleErrors(data);
          })
          .catch(() => { this.saveLoading = false; });
      });
    },

    handleErrors(data) {
      if (data.errors) {
        this.serverSideErrors = {};
        data.errors.forEach((e) => { this.serverSideErrors[e.field] = e.message; });
      } else this.showError(data.message);
    },

    showError(msg) {
      this.setAlert({ showAlert: true, variant: "red accent-3", timeout: "2000", message: msg });
    },
  },

  mounted() {
    this.getFlats();
    this.getFlatMember();
    this.setDefaultScopes();
  },
};
</script>

<style scoped>
.form-page-container { padding: 24px; width: 100%; margin: 0 auto; }
.form-section { padding: 20px 24px; }
.form-divider { height: 1px; background: var(--border, #e2e8f0); }
.half-width-wrap { max-width: 480px; }
@media (max-width: 960px) { .half-width-wrap { max-width: 100%; } }
@media (max-width: 600px) {
  .form-page-container { padding: 12px; }
  .form-section { padding: 16px 14px; }
}

.step-label {
  font-size: 11px; font-weight: 800; letter-spacing: 0.08em;
  color: var(--tx3, #94A3B8); text-transform: uppercase; margin-bottom: 16px;
}
.scope-question {
  font-size: 13.5px; font-weight: 600; color: var(--tx, #111827);
  margin-bottom: 16px; line-height: 1.4;
}
.hint-text { font-size: 11.5px; color: var(--tx3, #94A3B8); margin-top: 4px; padding-left: 2px; }

.scope-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  max-width: 600px;
}
.scope-card--full { grid-column: 1 / -1; }

/* On very small screens — single column */
@media (max-width: 480px) {
  .scope-grid {
    grid-template-columns: 1fr;
    gap: 8px;
    max-width: 100%;
  }
  .scope-card--full { grid-column: auto; }
}

.scope-card {
  display: flex; align-items: center; gap: 10px; padding: 12px 14px;
  border-radius: 10px; border: 1.5px solid var(--border, #E2E8F0);
  background: var(--surface2, #F8FAFC); cursor: pointer;
  transition: border-color 0.15s, background 0.15s; user-select: none;
  min-width: 0;
}
.scope-card:hover { border-color: var(--brand, #0D9972); }
.scope-card--active { border-color: var(--brand, #0D9972); background: rgba(13, 153, 114, 0.06); }
.scope-card--full { justify-content: space-between; }
.scope-card-left { display: flex; align-items: flex-start; gap: 10px; flex: 1; min-width: 0; }

/* Round = exclusive "All requests" */
.scope-checkbox {
  width: 18px; height: 18px; min-width: 18px; border-radius: 50%;
  border: 1.5px solid var(--border, #CBD5E1); background: #fff;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.15s, border-color 0.15s; flex-shrink: 0;
}
/* Square = independent multi-select */
.scope-checkbox--sq { border-radius: 4px; }
.scope-checkbox.checked { background: var(--brand, #0D9972); border-color: var(--brand, #0D9972); }

.scope-title {
  font-size: 13px; font-weight: 600; color: var(--tx, #111827);
  word-break: break-word;
}
.scope-desc {
  font-size: 11.5px; color: var(--tx3, #64748B); margin-top: 2px;
  line-height: 1.4; word-break: break-word;
}
.scope-badge {
  font-size: 10px; font-weight: 800; letter-spacing: 0.06em; padding: 3px 8px;
  border-radius: 20px; background: var(--brand, #0D9972); color: #fff;
  flex-shrink: 0; white-space: nowrap;
}
.scope-side-hint {
  font-size: 11px; color: var(--tx3, #94A3B8);
  margin-left: auto; padding-left: 8px; white-space: nowrap; flex-shrink: 0;
}

.scope-info-box {
  display: flex; align-items: flex-start; gap: 4px; margin-top: 14px;
  padding: 10px 14px; border-radius: 8px;
  background: var(--surface2, #F8FAFC); border: 1px solid var(--border, #E2E8F0);
  font-size: 12px; color: var(--tx2, #374151); max-width: 600px;
}
.scope-summary {
  display: flex; align-items: center; margin-top: 16px;
  font-size: 12.5px; color: var(--tx3, #64748B); font-weight: 500;
}
</style>
